import { useState, useCallback } from 'react'
import apiService from '../../../services/ApiService'

const pageSize = 10

export const useBlogsManagement = () => {
  const [blogs, setBlogs] = useState([])
  const [reviewBlogs, setReviewBlogs] = useState([])
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(false)
  const [reviewLoading, setReviewLoading] = useState(false)
  const [metricsLoading, setMetricsLoading] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [reviewPage, setReviewPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [reviewTotalPages, setReviewTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [reviewTotalItems, setReviewTotalItems] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [reviewSearchTerm, setReviewSearchTerm] = useState('')
  const [reviewStatusFilter, setReviewStatusFilter] = useState(
    'PENDING_REVIEW,REWORK_REQUIRED,PUBLISHED'
  )

  const normalizePageResponse = (response, fallbackSetter, fallbackTotalSetter) => {
    if (response?.content) {
      fallbackSetter(response.content)
      fallbackTotalSetter(response.totalElements || 0)
      return {
        totalPages: response.totalPages || 1,
        totalItems: response.totalElements || 0,
      }
    }
    if (Array.isArray(response)) {
      fallbackSetter(response)
      fallbackTotalSetter(response.length)
      return {
        totalPages: Math.max(1, Math.ceil(response.length / pageSize)),
        totalItems: response.length,
      }
    }
    fallbackSetter([])
    fallbackTotalSetter(0)
    return { totalPages: 1, totalItems: 0 }
  }

  const loadBlogs = useCallback(
    async (page = currentPage, search = searchTerm) => {
      try {
        setLoading(true)
        const response = await apiService.getAdminBlogs({
          page: page - 1,
          size: pageSize,
          search: search || undefined,
          scope: 'TEAM',
        })
        const normalized = normalizePageResponse(response, setBlogs, setTotalItems)
        setTotalPages(normalized.totalPages)
      } catch (error) {
        console.error('Failed to load team blogs:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [currentPage, searchTerm]
  )

  const loadReviewBlogs = useCallback(
    async (
      page = reviewPage,
      search = reviewSearchTerm,
      status = reviewStatusFilter
    ) => {
      try {
        setReviewLoading(true)
        const response = await apiService.getAdminBlogs({
          page: page - 1,
          size: pageSize,
          search: search || undefined,
          scope: 'REVIEW',
          status,
        })
        const normalized = normalizePageResponse(
          response,
          setReviewBlogs,
          setReviewTotalItems
        )
        setReviewTotalPages(normalized.totalPages)
      } catch (error) {
        console.error('Failed to load review blogs:', error)
        throw error
      } finally {
        setReviewLoading(false)
      }
    },
    [reviewPage, reviewSearchTerm, reviewStatusFilter]
  )

  const loadMetrics = useCallback(async () => {
    try {
      setMetricsLoading(true)
      const response = await apiService.getAdminBlogsMetrics()
      setMetrics(response)
    } catch (error) {
      console.error('Failed to load blog metrics:', error)
      throw error
    } finally {
      setMetricsLoading(false)
    }
  }, [])

  const createBlog = useCallback(async (blogData) => {
    const payload = {
      ...blogData,
      authorType: 'TEAM_GTL',
      reviewStatus: 'PUBLISHED',
      published: true,
    }
    await apiService.createAdminBlog(payload)
    await Promise.all([loadBlogs(1, searchTerm), loadMetrics()])
  }, [loadBlogs, loadMetrics, searchTerm])

  const updateBlog = useCallback(async (blogId, blogData) => {
    await apiService.updateAdminBlog(blogId, blogData)
    await Promise.all([loadBlogs(currentPage, searchTerm), loadMetrics()])
  }, [currentPage, loadBlogs, loadMetrics, searchTerm])

  const deleteBlog = useCallback(async (blogId) => {
    await apiService.deleteAdminBlog(blogId)
    await Promise.all([loadBlogs(currentPage, searchTerm), loadMetrics()])
  }, [currentPage, loadBlogs, loadMetrics, searchTerm])

  const reviewBlog = useCallback(
    async (blogId, action, notes = '') => {
      await apiService.reviewAdminBlog(blogId, { action, notes })
      await Promise.all([
        loadReviewBlogs(reviewPage, reviewSearchTerm, reviewStatusFilter),
        loadMetrics(),
      ])
    },
    [loadMetrics, loadReviewBlogs, reviewPage, reviewSearchTerm, reviewStatusFilter]
  )

  const handleSearch = useCallback(
    (search) => {
      setSearchTerm(search)
      setCurrentPage(1)
      loadBlogs(1, search)
    },
    [loadBlogs]
  )

  const handleReviewSearch = useCallback(
    (search) => {
      setReviewSearchTerm(search)
      setReviewPage(1)
      loadReviewBlogs(1, search, reviewStatusFilter)
    },
    [loadReviewBlogs, reviewStatusFilter]
  )

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page)
      loadBlogs(page, searchTerm)
    },
    [loadBlogs, searchTerm]
  )

  const handleReviewPageChange = useCallback(
    (page) => {
      setReviewPage(page)
      loadReviewBlogs(page, reviewSearchTerm, reviewStatusFilter)
    },
    [loadReviewBlogs, reviewSearchTerm, reviewStatusFilter]
  )

  const handleReviewStatusFilter = useCallback(
    (status) => {
      setReviewStatusFilter(status)
      setReviewPage(1)
      loadReviewBlogs(1, reviewSearchTerm, status)
    },
    [loadReviewBlogs, reviewSearchTerm]
  )

  return {
    blogs,
    reviewBlogs,
    metrics,
    loading,
    reviewLoading,
    metricsLoading,
    currentPage,
    reviewPage,
    totalPages,
    reviewTotalPages,
    totalItems,
    reviewTotalItems,
    searchTerm,
    setSearchTerm,
    reviewSearchTerm,
    setReviewSearchTerm,
    reviewStatusFilter,
    loadBlogs,
    loadReviewBlogs,
    loadMetrics,
    createBlog,
    updateBlog,
    deleteBlog,
    reviewBlog,
    handleSearch,
    handleReviewSearch,
    handlePageChange,
    handleReviewPageChange,
    handleReviewStatusFilter,
  }
}
